import { useMsal } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import msalConfig from '../auth/msalConfig.js';
import '../CSS/Navbar.css';
import logo from "../Images/logo1.png";
import Navigation from './Navigation.js';
import HighlightsNavigation from './HighlightsNavigation.js';
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { setStorage } from '../Components/Storage.js';
import ReportsDD from './ReportsDropdown.js';
import useIsMobile from '../Components/UseIsMobile.js';
import MediaDD from '../Components/MediaNavigation.js';

const msalInstance = new PublicClientApplication(msalConfig);

const Navbar = () => {
  const { instance, accounts } = useMsal();
  const isMsalAuthenticated = accounts.length > 0;
  const isCustomAuthenticated = localStorage.getItem('custom-auth') === 'true';
  const [clients, setClients] = useState([]);
  const [clientLink, setClientLink] = useState(null);
  const [customDisplayName, setCustomDisplayName] = useState('');
  const isMobile = useIsMobile();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  // Unified email from localStorage
  const unifiedEmail = localStorage.getItem('user-email');

  const activeUsername = unifiedEmail || null;

  const activeDisplayName = isMsalAuthenticated
    ? accounts[0].name || unifiedEmail
    : customDisplayName || unifiedEmail;

  const isAuthenticated = !!activeUsername;

  useEffect(() => {
    fetch('/api/hello')
      .then((response) => response.json())
      .then((data) => setClients(data.clients))
      .catch((error) => console.error('Error fetching client data:', error));
  }, []);

  useEffect(() => {
    if (activeUsername && clients.length > 0) {
      setStorage('username', activeUsername);
      const matchingClient = clients.find(
        (client) => client.fields.Email === activeUsername
      );

      if (matchingClient?.fields?.SpecCompany) {
        const company = matchingClient.fields.Company;
        setClientLink(
          <a className="navbar-item" key={company} href={`/${company}`}>
            {company}
          </a>
        );
      }

      if (!isMsalAuthenticated && matchingClient?.fields?.Title) {
        setCustomDisplayName(matchingClient.fields.Title);
      }
    }
  }, [clients, activeUsername, isMsalAuthenticated]);

  const logout = async () => {
    try {
      // Always tell the server to clear HttpOnly cookies
      await fetch('/api/logout', {
        method: 'POST',
        credentials: 'include'
      });
      
    } catch (e) {
      // ignore network hiccups; still continue local cleanup
    } finally {
      // Local fallback cleanup (if anything is still in storage)
      localStorage.removeItem('custom-auth');
      localStorage.removeItem('custom-email');
      localStorage.removeItem('user-email');
  
      if (isMsalAuthenticated) {
        // Redirect is more reliable than popup for full account sign-out
        await instance.logoutRedirect({ account: instance.getActiveAccount() });
        return;
      }
  
      // Custom auth path
      window.location.href = '/login';
    }
  };
  

  const AuthSection = (
    <div className="user-info">
      <p>Hello, {activeDisplayName}!</p>
      <button className="logout-btn" onClick={logout}>
        Logout
      </button>
    </div>
  );

  const UnauthSection = (
    <div className="user-info">
      <p>You are not logged in.</p>
      <button
        className="login-btn"
        onClick={() => (window.location.href = '/login')}
      >
        Login
      </button>
    </div>
  );

  const desktopNavbar = (
    <nav className="navbar">
      <a href="/">
        <img src={logo} className="logo" alt="Logo" />
      </a>
      <div className="navbar-menu">
        <a className="navbar-item" href="/">Home</a>
        <ReportsDD className="navigation" />
        <a className='navbar-item' href = "/arhub"> Risk Hub</a>
        <a className="navbar-item" href="/upcoming">Upcoming</a>
        <a className="navbar-item" href="/orgs">Groups</a>
        <HighlightsNavigation className="navigation" />
        <MediaDD className="navigation" /> 
        {clientLink && <div className="navbar-item client-link">{clientLink}</div>}
      </div>
      {isAuthenticated ? AuthSection : UnauthSection}
    </nav>
  );

  const mobileNavbar = (
    <nav className="mobile-navbar">
      <div className="mobile-navbar-header">
        <a href="/">
          <img src={logo} className="mobile-logo" alt="Logo" />
        </a>
        <button className="menu-toggle" onClick={() => setIsMenuOpen(!isMenuOpen)}>
          ☰
        </button>
      </div>
      {isMenuOpen && (
        <div className="mobile-menu">
          <a className="mobile-nav-item" href="/">Home</a>
          <ReportsDD className="mobile-navigation" />
          <a className="mobile-nav-item" href="/upcoming">Upcoming</a>
          <a className="mobile-nav-item" href="/orgs">Groups</a>
          <HighlightsNavigation className="mobile-nav-item" />
          <a className="mobile-nav-item" href="/powerbi">Statistics</a>
          {clientLink && <div className="mobile-nav-item client-link">{clientLink}</div>}
          {isAuthenticated ? AuthSection : UnauthSection}
        </div>
      )}
    </nav>
  );

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  return isMobile ? mobileNavbar : desktopNavbar;
};

export default Navbar;
